/**
 * Bulk SEO Editor
 * Spreadsheet-style editor for all pages' meta title/description/keywords
 */

import React, { useState, useCallback } from 'react';
import { Save, AlertTriangle, CheckCircle2 } from 'lucide-react';

// ============================================================================
// TYPES
// ============================================================================

interface PageSEOData {
  id: string;
  title: string;
  slug: string;
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
  ogImage: string;
  noIndex: boolean;
}

interface BulkSEOEditorProps {
  pages: PageSEOData[];
  onSave: (updates: { id: string; changes: Partial<PageSEOData> }[]) => Promise<void>;
}

function getTitleStatus(title: string): { color: string; message: string } {
  if (!title) return { color: 'text-red-500', message: 'Missing' };
  if (title.length < 30) return { color: 'text-yellow-500', message: `${title.length}/60 (short)` };
  if (title.length > 60) return { color: 'text-yellow-500', message: `${title.length}/60 (long)` };
  return { color: 'text-green-500', message: `${title.length}/60` };
}

function getDescStatus(desc: string): { color: string; message: string } {
  if (!desc) return { color: 'text-red-500', message: 'Missing' };
  if (desc.length < 120) return { color: 'text-yellow-500', message: `${desc.length}/160 (short)` };
  if (desc.length > 160) return { color: 'text-yellow-500', message: `${desc.length}/160 (long)` };
  return { color: 'text-green-500', message: `${desc.length}/160` };
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function BulkSEOEditor({ pages: initialPages, onSave }: BulkSEOEditorProps) {
  const [pages, setPages] = useState(initialPages);
  const [modified, setModified] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);

  const updatePage = (pageId: string, field: keyof PageSEOData, value: any) => {
    setPages(prev => prev.map(p =>
      p.id === pageId ? { ...p, [field]: value } : p
    ));
    setModified(prev => new Set(prev).add(pageId));
  };

  const handleSave = async () => {
    if (modified.size === 0) return;
    setIsSaving(true);

    const updates = Array.from(modified).map(id => {
      const page = pages.find(p => p.id === id);
      if (!page) return null;
      return {
        id,
        changes: {
          metaTitle: page.metaTitle,
          metaDescription: page.metaDescription,
          metaKeywords: page.metaKeywords,
          ogImage: page.ogImage,
          noIndex: page.noIndex,
        },
      };
    }).filter(Boolean) as { id: string; changes: Partial<PageSEOData> }[];

    try {
      await onSave(updates);
      setModified(new Set());
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-900">Bulk Meta Editor</h3>
          <p className="text-sm text-gray-500">{pages.length} pages</p>
        </div>
        <button
          onClick={handleSave}
          disabled={modified.size === 0 || isSaving}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
        >
          <Save className="h-4 w-4" />
          Save {modified.size > 0 ? `(${modified.size})` : ''}
        </button>
      </div>

      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3 w-40">Page</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Meta Title</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Meta Description</th>
              <th className="text-center text-xs font-medium text-gray-500 uppercase px-4 py-3 w-16">No Index</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {pages.map((page) => {
              const titleStatus = getTitleStatus(page.metaTitle);
              const descStatus = getDescStatus(page.metaDescription);
              const isModified = modified.has(page.id);

              return (
                <tr key={page.id} className={isModified ? 'bg-yellow-50' : 'hover:bg-gray-50'}>
                  <td className="px-4 py-2">
                    <div className="text-sm font-medium text-gray-900 truncate">{page.title}</div>
                    <div className="text-xs text-gray-400 font-mono truncate">{page.slug}</div>
                  </td>
                  <td className="px-4 py-2">
                    <input
                      type="text"
                      value={page.metaTitle}
                      onChange={(e) => updatePage(page.id, 'metaTitle', e.target.value)}
                      placeholder="Meta title..."
                      className="w-full border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 px-1 py-1 text-sm bg-transparent"
                    />
                    <span className={`text-xs ${titleStatus.color}`}>{titleStatus.message}</span>
                  </td>
                  <td className="px-4 py-2">
                    <textarea
                      value={page.metaDescription}
                      onChange={(e) => updatePage(page.id, 'metaDescription', e.target.value)}
                      placeholder="Meta description..."
                      className="w-full border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 px-1 py-1 text-sm bg-transparent resize-none h-12"
                    />
                    <span className={`text-xs ${descStatus.color}`}>{descStatus.message}</span>
                  </td>
                  <td className="text-center px-4 py-2">
                    <input
                      type="checkbox"
                      checked={page.noIndex}
                      onChange={(e) => updatePage(page.id, 'noIndex', e.target.checked)}
                      className="rounded"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default BulkSEOEditor;
